import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  ClipboardCheck,
  Wallet,
  Coins,
  BarChart3,
  ChevronRight,
  Check,
  AlertTriangle,
  CircleDot,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Receipt,
  CheckCircle2,
  Home,
  Sun,
  // Sprint 137 — El dinero que anda en la calle con la moto.
  Bike,
} from 'lucide-react';
import type {
  CorteCajaVista,
  ResumenDiaVista,
  CierreDiaVista,
  AplicacionPromoVista,
  PendientesCierre,
  PendienteCierre,
} from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { formatPrecio } from '@renderer/utils/formatters';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourCerrarDia } from '@renderer/tours';
import { PropinasPage } from './PropinasPage';
import { CorteCajaPage } from './CorteCajaPage';
import { CierreDelDiaPage } from './CierreDelDiaPage';
import { calcularVariante, type VarianteDia as VarianteDiaPura } from '@renderer/utils/varianteDia';
import { StepUpModal } from '@renderer/components/auth/StepUpModal';
import { leerDineroTecleado } from '@shared/dineroTecleado';
import { useStepUp } from '@renderer/hooks/useStepUp';
import { toLocalYYYYMMDD } from '@renderer/utils/dateLocal';

/**
 * Sprint 23.2 + 24 — Wizard unificado "Cerrar Día".
 *
 * Sprint 24: pasa de "navegar a páginas" a "embeber Contenido inline".
 * Cada paso es una sección expandible que monta la página correspondiente
 * con `embebido=true` (oculta su header). La dueña hace TODO el flujo sin
 * salir del wizard.
 *
 * Sprint 114 — **Este wizard es ahora la puerta** de propinas y cierre de existencias.
 *
 * El sub-menú "Avanzado" del sidebar desapareció con el menú agrupado. `/corte-caja` subió al
 * bloque "Abrir y cerrar" —abrir la caja es lo primero que se hace en el día y estaba a dos clics
 * detrás de una palabra que aleja a quien apenas aprende—, y `/propinas` y `/cierre-dia` se
 * alcanzan desde aquí, que es cuando de verdad se usan. Las rutas siguen vivas para quien las
 * tenga enlazadas.
 */

type EstadoPaso = 'hecho' | 'pendiente' | 'saltado';


// Sprint 42 — Variante visual del día. Lógica pura en `utils/varianteDia.ts`.
type VarianteDia = VarianteDiaPura;

const PASOS = [
  { id: 1, titulo: 'Revisa el día',         icon: BarChart3 },
  { id: 2, titulo: 'Reparto de Propinas',   icon: Coins },
  { id: 3, titulo: 'Corte de Caja',         icon: Wallet },
  { id: 4, titulo: 'Cierre de existencias', icon: ClipboardCheck },
] as const;

export function CerrarDiaWizardPage() {
  const navigate = useNavigate();
  const sesion = useSessionStore((s) => s.sesion);
  const negocio = useNegocioStore((s) => s.negocio);
  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const [resumen, setResumen] = useState<ResumenDiaVista | null>(null);
  const [corte, setCorte] = useState<CorteCajaVista | null>(null);
  const [pozoPendiente, setPozoPendiente] = useState<number>(0);
  const [cierreHoy, setCierreHoy] = useState<CierreDiaVista | null>(null);
  // Sprint 58 — Actividad viva al cerrar: mesas, cocina y entregas. Antes esto
  // era un simple contador de pedidos en cocina y por eso una mesa abierta desde
  // el día anterior (16 h, reporte de campo) no aparecía por ningún lado.
  const [pendientes, setPendientes] = useState<PendientesCierre | null>(null);
  // Sprint 24 — promos aplicadas hoy (suma de aplicaciones_promo del día).
  const [promosAplicadasHoy, setPromosAplicadasHoy] = useState<AplicacionPromoVista[]>([]);
  // Sprint 42 — marca persistente "día cerrado" leída de ConfiguracionSistema.
  const [diaCerradoMarca, setDiaCerradoMarca] = useState<{ fechaCierre: string } | null>(null);
  // Sprint 42 — cuando la dueña en variante C decide volver a revisar pasos.
  const [verPasosForzado, setVerPasosForzado] = useState<boolean>(false);
  /**
   * Paso expandido (-1 = ninguno).
   *
   * Sprint 144 (auditoría de interfaz) — **Arranca cerrado.** El Paso 1 venía desplegado con sus
   * cuatro mosaicos de cifras, y debajo de la tarjeta "Terminar mi día" eso pesaba tanto como el
   * camino: lo que se veía al llegar no era "una cosa que hacer y un anexo", sino cinco tarjetas
   * apiladas. Peor: sus ventas son **del día de calendario** y las de la tarjeta **desde que se
   * abrió la caja**, así que la misma pantalla mostraba dos totales distintos uno debajo del otro.
   *
   * Se abre en cuanto ella lo toca, que es cuando de verdad quiere el detalle.
   */
  const [expandido, setExpandido] = useState<number>(-1);
  const [saltados, setSaltados] = useState<Set<number>>(() => {
    try {
      const raw = sessionStorage.getItem('cerrar-dia-saltados');
      return raw ? new Set(JSON.parse(raw) as number[]) : new Set();
    } catch {
      return new Set();
    }
  });
  const [cargando, setCargando] = useState(true);
  const [cerrando, setCerrando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    const [resR, corteR, pozoR, histR, pedR, promosR, marcaR] = await Promise.all([
      window.api.reportes.ventasDelDia(),
      window.api.corte.abierto(),
      window.api.propinas.pozo(),
      window.api.cierreDia.historial(5),
      window.api.cierreDia.pendientes(),
      window.api.reportes.promosAplicadasHoy().catch(() => ({ ok: false } as const)),
      // Sprint 42 — leer marca persistente
      window.api.cierreDia.estaCerradoHoy(),
    ]);
    if (resR.ok) setResumen(resR.data);
    if (corteR.ok) setCorte(corteR.data);
    if (pozoR.ok) setPozoPendiente(pozoR.data.totalPendiente ?? 0);
    if (histR.ok) {
      // Sprint 44 — comparar fechas en zona horaria LOCAL, no UTC.
      // Bug raíz reportado 2026-05-24 21:43: a las 21:43 hora México (UTC-6),
      // `new Date().toISOString()` daba "2026-05-25" mientras que la fila
      // `cierres_dia.fecha` quedaba como "2026-05-24T06:00:00.000Z" (medianoche
      // local México en UTC). La comparación nunca matcheaba y el wizard
      // declaraba "Paso 4 pendiente" aunque el inventario sí se había cerrado.
      const hoy = toLocalYYYYMMDD(new Date());
      const cierre = histR.data.find((c) => toLocalYYYYMMDD(new Date(c.fecha)) === hoy);
      setCierreHoy(cierre ?? null);
    }
    if (pedR.ok) setPendientes(pedR.data);
    if (promosR.ok && Array.isArray(promosR.data)) setPromosAplicadasHoy(promosR.data);
    if (marcaR.ok) setDiaCerradoMarca(marcaR.data);
    setCargando(false);
  };

  useEffect(() => {
    cargar();
  }, []);

  /**
   * Sprint 39 + 43 — Cuando el usuario expande O colapsa un paso, recargar
   * estado desde backend. Antes solo recargaba al colapsar — pero si la
   * dueña reparte propinas DENTRO del paso 2 sin colapsar, el wizard
   * seguía con el pozo viejo. Ahora también refresca al expandir, y los
   * hijos llaman `onCambio` (que dispara `cargar()`) tras cada mutación.
   */
  const cambiarExpandido = async (nuevo: number) => {
    const anterior = expandido;
    setExpandido(nuevo);
    if (nuevo !== anterior) {
      await cargar();
    }
  };

  const guardarSaltados = (next: Set<number>) => {
    setSaltados(next);
    try {
      sessionStorage.setItem('cerrar-dia-saltados', JSON.stringify([...next]));
    } catch {
      // ignorar
    }
  };

  const togglearSaltado = (id: number) => {
    const next = new Set(saltados);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    guardarSaltados(next);
  };

  const estadoPaso = (id: number): EstadoPaso => {
    if (saltados.has(id)) return 'saltado';
    switch (id) {
      case 1: return resumen ? 'hecho' : 'pendiente';
      case 2: return pozoPendiente === 0 ? 'hecho' : 'pendiente';
      case 3: return corte === null ? 'hecho' : 'pendiente';
      case 4: return cierreHoy ? 'hecho' : 'pendiente';
      default: return 'pendiente';
    }
  };

  /**
   * Sprint 113 — Qué se puede saltar, dicho UNA vez.
   *
   * Antes esto vivía suelto en cada `<PasoCard puedeSaltarse={…}>`. Al ofrecer también el atajo
   * desde el aviso de "te falta esto", dos sitios tendrían que opinar lo mismo sobre la misma
   * regla — y el día que uno cambie, el otro ofrecería un botón que no hace nada, o escondería
   * una salida que sí existe.
   *
   * **Los que NO se pueden saltar tampoco son un olvido:** el paso 3 es el dinero del día, y los
   * pedidos abiertos arrastran consumo al día siguiente.
   */
  const puedeSaltarsePaso = (id: number): boolean => {
    switch (id) {
      case 2: return pozoPendiente > 0; // propinas sin repartir
      case 4: return !cierreHoy; // inventario sin cerrar
      default: return false; // 1 (resumen) y 3 (corte de caja) jamás
    }
  };

  const todoListo = [1, 2, 3, 4].every((id) => estadoPaso(id) !== 'pendiente');

  // Sprint 42 — Variante calculada con función pura (testeable).
  const variante: VarianteDia = calcularVariante({
    diaCerradoMarca,
    cajaCerrada: corte === null,
    propinasRepartidas: pozoPendiente === 0,
    inventarioCerrado: cierreHoy !== null,
  });

  const cerrarDia = async () => {
    if (!sesion) return;
    // Sprint 43 — eliminado el `window.confirmar` aquí. Causaba "el botón no
    // hace nada" si el modal salía fuera de viewport o si la dueña apretaba
    // cancelar sin querer. El botón solo está disponible cuando todoListo
    // es true Y no hay pedidos abiertos — ese gating YA es la confirmación.
    // Si el contrato cambia (ej. reapertura), volver a poner.
    setCerrando(true);
    const r = await window.api.cierreDia.marcarCerrado();
    setCerrando(false);
    if (!r.ok) {
      window.alertar({
        titulo: 'No se pudo cerrar el día',
        mensaje: r.error,
        variant: 'warn',
      });
      return;
    }
    sessionStorage.removeItem('cerrar-dia-saltados');
    setDiaCerradoMarca(r.data);
    setVerPasosForzado(false);
    // No redirect automático; la variante C tiene botón explícito al dashboard.
  };

  // ------------------------------------------------------------------------
  // Sprint 42 — VARIANTE C: día cerrado operativamente.
  // Si la dueña pidió "Revisar pasos del cierre" desde la variante C,
  // se muestra el wizard normal con un banner verde persistente arriba.
  // Si no, se muestra el panel grande de éxito con CTA al dashboard.
  // ------------------------------------------------------------------------
  if (variante === 'cerrado' && !verPasosForzado) {
    return (
      <DiaCerradoPanel
        fechaCierre={diaCerradoMarca!.fechaCierre}
        resumen={resumen}
        monedaSimbolo={monedaSimbolo}
        locale={locale}
        onIrDashboard={() => navigate('/dashboard')}
        onRevisarPasos={() => setVerPasosForzado(true)}
        onVerReporte={() => navigate('/reportes')}
      />
    );
  }

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      {/* Sprint 42 — Banner verde persistente cuando la marca está pero la
          dueña pidió revisar pasos. No se cierra solo. */}
      {variante === 'cerrado' && verPasosForzado && diaCerradoMarca && (
        <div className="rounded-xl border-2 border-emerald-400 bg-emerald-50 p-4 flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-emerald-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-emerald-900">
              Día cerrado a las {new Date(diaCerradoMarca.fechaCierre).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="text-sm text-emerald-800">
              Estás revisando los pasos del cierre. La marca se mantiene.
            </div>
          </div>
          <button
            onClick={() => { setVerPasosForzado(false); }}
            className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            Volver al panel de cierre
          </button>
        </div>
      )}

      <header className="flex items-center gap-3" data-tour="cd-header">
        <Lock className="h-7 w-7 text-primary" />
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Cerrar Día</h1>
          <p className="text-sm text-slate-500">
            {variante === 'inicial'
              ? 'Antes de empezar: caja abierta, día activo. Cuando termines, vuelve aquí.'
              : variante === 'en_proceso'
              ? 'Vas avanzando — revisa, reparte propinas, corta caja, cierra inventario.'
              : 'Flujo guiado: revisa, reparte propinas, corta caja, cierra inventario.'}
          </p>
        </div>
        <button onClick={cargar} className="btn-ghost text-sm">Refrescar</button>
        <HelpButton tour={tourCerrarDia} />
      </header>

      <div data-tour="cd-stepper">
        <StepperBar pasos={PASOS} estados={PASOS.map((p) => estadoPaso(p.id))} />
      </div>

      {pendientes && pendientes.total > 0 && (
        <PendientesCierrePanel pendientes={pendientes} onIrAPedidos={() => navigate('/pedidos')} />
      )}

      {/*
        Sprint 137 — El dinero que anda en la calle: se reporta, no frena.

        Va en su propio panel y en otro color (violeta, no ámbar) porque **no es una tarea
        pendiente**: es una cuenta por cobrar. Mezclarlo con las mesas abiertas y la comida en la
        parrilla enseñaría a leer el aviso de arriba como "algo que se puede ignorar", que es
        justo lo contrario de lo que ese aviso necesita.
      */}
      {pendientes && (pendientes.porCobrarEnLaCalle?.length ?? 0) > 0 && (
        <PorCobrarEnLaCallePanel
          items={pendientes.porCobrarEnLaCalle ?? []}
          monto={pendientes.montoPorCobrarEnLaCalle ?? 0}
          monedaSimbolo={monedaSimbolo}
          locale={locale}
          onIrADomicilios={() => navigate('/domicilios')}
        />
      )}

      {/*
        Sprint 144 — **El camino corto, para quien sólo quiere irse a su casa.**

        Reportado por el dueño el 2026-09-19: la dueña **no cierra caja desde el 11 de septiembre**
        —ocho días acumulados, cero cierres—. Sus dos razones, y las dos son ciertas:

        > *"Con el sistema original no estaba acostumbrada a hacerlo"* y *"cerrar caja está muy
        > laborioso para ella, que es el final de la jornada y ya se quiere ir, ya tiene cansancio
        > mental y físico"*.

        Lo que este tablero le pide a las once de la noche son cuatro pasos, decidir cuáles puede
        saltarse, contar el efectivo billete por billete y el código de su teléfono. De todo eso,
        **lo único obligatorio es el corte de caja**; el resto es opcional y ella no tiene por qué
        saberlo.

        Así que arriba de los pasos va lo que de verdad necesita: cuánto entró, una sola pregunta
        opcional sobre el dinero de la gaveta, y un botón. El detalle sigue abajo, intacto, para
        cuando quiera mirarlo.

        **El código sigue pidiéndose** — decisión de José, 2026-09-19: *"que siga pidiendo el
        código, no debemos aflojar en la seguridad"*. Lo que cambia es que se avisa **antes**, para
        que tenga el teléfono en la mano en vez de descubrirlo al final.
      */}
      {!cargando && corte !== null && (
        <CerrarMiDiaCard
          corte={corte}
          hayPendientes={(pendientes?.total ?? 0) > 0}
          cuantosPendientes={pendientes?.total ?? 0}
          monedaSimbolo={monedaSimbolo}
          locale={locale}
          onIrAPedidos={() => navigate('/pedidos')}
          onListo={async () => {
            sessionStorage.removeItem('cerrar-dia-saltados');
            await cargar();
          }}
        />
      )}

      {cargando ? (
        <div className="card text-center py-8 text-slate-400">Cargando estado del día…</div>
      ) : (
        <div className="space-y-3 pt-6" data-tour="cd-pasos">
          {/*
            Sprint 144 (auditoría) — La frontera entre el camino y el anexo.

            La tarjeta y los pasos se separaban 16 px, contra los 12 px que separan a los pasos
            entre sí: cuatro píxeles no construyen una frontera. Con el aire y este encabezado, lo
            de abajo se lee como lo que es —el detalle, para quien lo quiera— y no como el
            siguiente trámite obligatorio.
          */}
          {corte !== null && (
            <p className="text-sm font-semibold text-slate-600">Si quieres ver el detalle del día</p>
          )}
          {/* Paso 1 — Resumen + promos aplicadas hoy */}
          <PasoCard
            paso={PASOS[0]}
            estado={estadoPaso(1)}
            abierto={expandido === 1}
            onToggle={() => cambiarExpandido(expandido === 1 ? -1 : 1)}
            resumen={
              resumen
                ? /*
                     Sprint 144 (segunda auditoría) — Dice DE CUÁNDO habla.

                     Este resumen es del día de calendario y la tarjeta de arriba acumula desde
                     que se abrió la caja: con ocho días abierta, dos totales de ventas distintos
                     a 130 px uno del otro, sin que nada explicara la diferencia. Colapsar el paso
                     no bastó, porque la cifra vive en su cabecera cerrada.
                   */
                  `Hoy: ${formatPrecio(resumen.totalVentas, monedaSimbolo, locale)} en ventas · ${resumen.cantidadPedidosPagados} pedidos`
                : 'Sin datos del día.'
            }
            puedeSaltarse={puedeSaltarsePaso(1)}
            saltado={saltados.has(1)}
            onToggleSaltado={() => togglearSaltado(1)}
          >
            {resumen && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                  <Mini label="Ventas" valor={formatPrecio(resumen.totalVentas, monedaSimbolo, locale)} />
                  <Mini label="Pedidos" valor={String(resumen.cantidadPedidosPagados)} />
                  <Mini label="Tkt. prom." valor={formatPrecio(resumen.ticketPromedio, monedaSimbolo, locale)} />
                  <Mini label="Efectivo" valor={formatPrecio(resumen.totalEfectivo, monedaSimbolo, locale)} />
                </div>
                {/* Sprint 24 — Promos aplicadas hoy */}
                <PromosAplicadasHoy promos={promosAplicadasHoy} monedaSimbolo={monedaSimbolo} locale={locale} />
                <button
                  onClick={() => navigate('/reportes')}
                  className="btn-ghost text-xs"
                >
                  <Receipt className="h-3.5 w-3.5" /> Ver reporte completo
                </button>
              </div>
            )}
          </PasoCard>

          {/* Paso 2 — Propinas */}
          <PasoCard
            paso={PASOS[1]}
            estado={estadoPaso(2)}
            abierto={expandido === 2}
            onToggle={() => cambiarExpandido(expandido === 2 ? -1 : 2)}
            resumen={
              pozoPendiente > 0
                ? `${formatPrecio(pozoPendiente, monedaSimbolo, locale)} pendientes de repartir`
                : 'Sin propinas pendientes.'
            }
            puedeSaltarse={puedeSaltarsePaso(2)}
            saltado={saltados.has(2)}
            onToggleSaltado={() => togglearSaltado(2)}
            hintSaltado="Las propinas quedan guardadas. Puedes repartirlas otro día desde el menú 'Propinas'."
          >
            {/* Sprint 39 — botón gigante para guardar y seguir si no se reparte hoy. */}
            {pozoPendiente > 0 && !saltados.has(2) && (
              <div className="mb-3 rounded-lg bg-amber-50 border-2 border-amber-200 p-3 space-y-2">
                <div className="text-sm text-amber-900">
                  Tienes <strong>{formatPrecio(pozoPendiente, monedaSimbolo, locale)}</strong> en propinas sin repartir.
                  ¿Las repartes hoy o las dejas para otro día?
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => togglearSaltado(2)}
                    className="btn-ghost flex-1 border-2 border-amber-300 bg-white"
                  >
                    💰 Guardar para otro día (no repartir ahora)
                  </button>
                  <div className="flex-1 text-xs text-amber-800 self-center text-center">
                    o repártelas abajo →
                  </div>
                </div>
              </div>
            )}
            {saltados.has(2) && (
              <div className="mb-3 rounded-lg bg-slate-100 border border-slate-300 p-3 text-sm text-slate-700 flex items-center justify-between">
                <span>
                  ⏭ Saltado: las propinas quedan guardadas para otro día.{' '}
                  <button onClick={() => togglearSaltado(2)} className="underline text-primary font-medium">
                    Cambiar de opinión
                  </button>
                </span>
              </div>
            )}
            <div className="border-t border-slate-200 pt-3">
              <PropinasPage embebido onCambio={cargar} />
            </div>
          </PasoCard>

          {/* Paso 3 — Corte de Caja */}
          <PasoCard
            paso={PASOS[2]}
            estado={estadoPaso(3)}
            abierto={expandido === 3}
            onToggle={() => cambiarExpandido(expandido === 3 ? -1 : 3)}
            resumen={
              corte
                ? `Caja abierta desde ${new Date(corte.fechaApertura).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })} (inicial ${formatPrecio(corte.montoInicial, monedaSimbolo, locale)})`
                : 'Caja cerrada.'
            }
            puedeSaltarse={puedeSaltarsePaso(3)}
            saltado={saltados.has(3)}
            onToggleSaltado={() => togglearSaltado(3)}
          >
            {corte && pozoPendiente > 0 && (
              <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                💡 <strong>Tip</strong>: reparte propinas en el paso 2 antes de cerrar caja, para que el
                efectivo esperado refleje lo correcto.
              </div>
            )}
            <div className="border-t border-slate-200 pt-3">
              <CorteCajaPage embebido onCambio={cargar} />
            </div>
          </PasoCard>

          {/* Paso 4 — Cierre de existencias */}
          <PasoCard
            paso={PASOS[3]}
            estado={estadoPaso(4)}
            abierto={expandido === 4}
            onToggle={() => cambiarExpandido(expandido === 4 ? -1 : 4)}
            resumen={
              cierreHoy
                ? `Cierre registrado hoy a las ${new Date(cierreHoy.fechaCreacion).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}.`
                : 'Aún sin cierre de inventario hoy.'
            }
            puedeSaltarse={puedeSaltarsePaso(4)}
            saltado={saltados.has(4)}
            onToggleSaltado={() => togglearSaltado(4)}
            hintSaltado="Tus existencias quedan como están. Puedes cerrarlas otro día."
          >
            {/* Sprint 39 — botón gigante "saltar" si no se cierra inventario hoy. */}
            {!cierreHoy && !saltados.has(4) && (
              <div className="mb-3 rounded-lg bg-amber-50 border-2 border-amber-200 p-3 space-y-2">
                <div className="text-sm text-amber-900">
                  Cerrar el inventario registra un snapshot de lo que vendiste hoy.
                  ¿Lo haces ahora o lo saltas?
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => togglearSaltado(4)}
                    className="btn-ghost flex-1 border-2 border-amber-300 bg-white"
                  >
                    📋 Saltar — cerrar inventario otro día
                  </button>
                  <div className="flex-1 text-xs text-amber-800 self-center text-center">
                    o ciérralo abajo ↓
                  </div>
                </div>
              </div>
            )}
            {saltados.has(4) && (
              <div className="mb-3 rounded-lg bg-slate-100 border border-slate-300 p-3 text-sm text-slate-700 flex items-center justify-between">
                <span>
                  ⏭ Saltado: el inventario no se cierra hoy.{' '}
                  <button onClick={() => togglearSaltado(4)} className="underline text-primary font-medium">
                    Cambiar de opinión
                  </button>
                </span>
              </div>
            )}
            <div className="border-t border-slate-200 pt-3">
              <CierreDelDiaPage embebido onCambio={cargar} />
            </div>
          </PasoCard>
        </div>
      )}

      {/* Sprint 43 — Bloque final REDISEÑADO con feedback honesto.
          ANTES: una franja gris con texto chico y botón rojo a la derecha.
          AHORA: card que CAMBIA de aspecto según el estado.
            - Si falta algo: card AMBAR grande, lista de pasos faltantes
              con check rojo/verde, botón "Cerrar día completo" DESHABILITADO
              + razón explícita. La dueña no adivina por qué no puede.
            - Si todo listo: card VERDE grande, botón gigante azul "Cerrar
              día completo" que dispara directo sin window.confirmar (que
              estaba causando el "no hace nada" si el modal salía mal).
       */}
      {/*
        Sprint 144 — Este aviso se calla mientras el camino corto está arriba.

        Decía *"te falta esto antes de cerrar el día"* y listaba el corte de caja, justo debajo de
        una tarjeta que dice *"lo demás de abajo es opcional"*. Dos guías a la vez, contándole
        cosas distintas del mismo día: quien no sabe a cuál hacer caso, no hace caso a ninguna.

        Manda la tarjeta, que es el camino. Los pasos siguen abajo con su estado, y este bloque
        vuelve entero —el aviso ámbar y el panel verde de "todo listo"— en cuanto la caja se cierre
        y el final del día dependa de lo que falte.

        **Se calla el bloque completo, no sólo el aviso.** Apagar únicamente la rama ámbar dejaba
        ver la otra, que afirma *"Todo listo. Puedes cerrar el día"* con la caja abierta: cambiar
        una contradicción por una mentira es peor negocio que dejarla como estaba.
      */}
      {corte !== null ? null : !todoListo || (pendientes?.total ?? 0) > 0 ? (
        <div
          className="rounded-2xl border-4 border-amber-400 bg-amber-50 p-5 sticky bottom-4 z-10 shadow-md"
          data-tour="cd-final"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-7 w-7 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-lg font-bold text-amber-900">
                Te falta esto antes de cerrar el día:
              </h3>
              <ul className="mt-2 space-y-1.5">
                {pendientes && pendientes.total > 0 && (
                  <li className="flex items-center gap-2 text-amber-900">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-600 text-white font-bold text-sm">!</span>
                    <span>
                      Queda <strong>{resumirPendientes(pendientes)}</strong> sin cerrar.{' '}
                      <button onClick={() => navigate('/pedidos')} className="underline font-semibold text-amber-900 hover:text-amber-700">
                        Ir a Pedidos →
                      </button>
                    </span>
                  </li>
                )}
                {[1, 2, 3, 4]
                  .filter((id) => estadoPaso(id) === 'pendiente')
                  .map((id) => {
                    const paso = PASOS[id - 1];
                    return (
                      <li key={id} className="flex items-center gap-2 text-amber-900">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-amber-600 bg-white text-amber-800 font-bold text-sm">
                          {id}
                        </span>
                        <span>
                          Paso {id}: <strong>{paso.titulo}</strong>.{' '}
                          <button
                            onClick={() => cambiarExpandido(id)}
                            className="underline font-semibold text-amber-900 hover:text-amber-700"
                          >
                            Ir a este paso ↓
                          </button>
                          {/*
                            Sprint 113 — La salida, donde está el problema.

                            Saltar propinas o inventario **ya se podía**, y por eso la dueña no lo
                            hacía: la casilla vive *dentro* del paso —hay que expandirlo para
                            verla— y la única pista era un párrafo en `text-xs` al final de esta
                            misma tarjeta. Tres pasos de descubrimiento para alguien que apenas
                            está aprendiendo el sistema.

                            Su frase, textual: *"a como puedo cierro el día y si no puedo pues ahí
                            se queda abierto"*. No es que no supiera cerrar: es que no sabía que
                            tenía permiso para cerrar sin esos dos pasos.

                            El botón se ofrece **sólo** en los pasos que de verdad se pueden
                            saltar. Pedidos abiertos y Corte de Caja no lo tienen, y no es un
                            descuido: uno arrastra consumo al día siguiente y el otro es el
                            dinero del día.
                          */}
                          {puedeSaltarsePaso(id) && (
                            <>
                              {' · '}
                              <button
                                onClick={() => togglearSaltado(id)}
                                /*
                                  Amarillo sobre amarillo no se lee como botón. Esto es lo que
                                  desatasca a quien no sabe hacer el paso, así que tiene que
                                  parecer tocable: fondo sólido, contraste real y 44 px de alto
                                  (Fitts), como el resto de las acciones del sistema.
                                */
                                className="ml-1 inline-flex min-h-[44px] items-center rounded-lg border-2 border-amber-700 bg-white px-3 py-1.5 font-bold text-amber-900 shadow-sm hover:bg-amber-100"
                              >
                                Saltarlo hoy →
                              </button>
                            </>
                          )}
                        </span>
                      </li>
                    );
                  })}
              </ul>
              {/*
                Sprint 113 — El texto describía el camino largo, y ahora hay uno corto.

                Decía cómo llegar a la casilla "dentro del paso, cuando lo expandes" — que era la
                única vía hasta hace un momento y ahora convive con el botón que está tres
                centímetros más arriba. Dejarlo así mandaría a dar la vuelta a quien ya tiene el
                atajo enfrente: el mismo defecto de "el texto que quedó atrás" que este sprint y
                el anterior vinieron corrigiendo. Ahora sólo dice lo que el botón no puede decir
                por sí mismo: **por qué a dos pasos no se les ofrece.**
              */}
              <p className="mt-3 text-xs text-amber-800">
                Lo que saltes queda pendiente y lo puedes hacer otro día. El{' '}
                <strong>Corte de Caja</strong> y los pedidos sin cobrar no se pueden saltar: uno es
                el dinero de hoy y los otros se arrastran a mañana.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div
          className="rounded-2xl border-4 border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100 p-5 sticky bottom-4 z-10 shadow-md"
          data-tour="cd-final"
        >
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-3 flex-1">
              <CheckCircle2 className="h-10 w-10 text-emerald-700 shrink-0" />
              <div>
                <h3 className="text-lg font-bold text-emerald-900">
                  Todo listo. Puedes cerrar el día.
                </h3>
                <p className="text-sm text-emerald-800">
                  Esto marcará el día como cerrado. Después podrás revisar el reporte cuando quieras.
                </p>
              </div>
            </div>
            <button
              onClick={cerrarDia}
              disabled={cerrando}
              className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-4 text-lg font-semibold text-white shadow-lg hover:bg-blue-700 active:scale-[0.98] disabled:opacity-60 disabled:cursor-wait transition w-full sm:w-auto"
            >
              <Lock className="h-6 w-6" />
              {cerrando ? 'Cerrando…' : 'Cerrar día completo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// StepperBar — visual top-of-page con los 4 pasos y sus estados.
// ============================================================================

function StepperBar({
  pasos,
  estados,
}: {
  pasos: ReadonlyArray<{ id: number; titulo: string; icon: typeof BarChart3 }>;
  estados: EstadoPaso[];
}) {
  return (
    <div className="card overflow-x-auto">
      <div className="flex items-center gap-2 min-w-max">
        {pasos.map((p, idx) => {
          const est = estados[idx];
          const Icon = p.icon;
          const color =
            est === 'hecho'
              ? 'bg-emerald-600 text-white border-emerald-600'
              : est === 'saltado'
              ? 'bg-slate-400 text-white border-slate-400'
              : 'bg-white text-primary border-primary';
          return (
            <div key={p.id} className="flex items-center gap-2">
              <div className={`flex items-center gap-2 rounded-full border-2 px-3 py-1.5 ${color}`}>
                {est === 'hecho' ? (
                  <Check className="h-4 w-4" />
                ) : est === 'saltado' ? (
                  <CircleDot className="h-4 w-4" />
                ) : (
                  <Icon className="h-4 w-4" />
                )}
                <span className="text-xs font-semibold">
                  {p.id}. {p.titulo}
                </span>
              </div>
              {idx < pasos.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// PasoCard — tarjeta colapsable con resumen one-line + contenido embebido.
// ============================================================================

function PasoCard({
  paso,
  estado,
  resumen,
  abierto,
  onToggle,
  puedeSaltarse,
  saltado,
  onToggleSaltado,
  hintSaltado,
  children,
}: {
  paso: { id: number; titulo: string; icon: typeof BarChart3 };
  estado: EstadoPaso;
  resumen: string;
  abierto: boolean;
  onToggle: () => void;
  puedeSaltarse: boolean;
  saltado: boolean;
  onToggleSaltado: () => void;
  hintSaltado?: string;
  children: React.ReactNode;
}) {
  const Icon = paso.icon;
  const estadoStyle =
    estado === 'hecho'
      ? 'border-emerald-300 bg-emerald-50'
      : estado === 'saltado'
      ? 'border-slate-300 bg-slate-50 opacity-75'
      : 'border-slate-200 bg-white';

  return (
    <div className={`rounded-xl border-2 ${estadoStyle}`}>
      {/* Header siempre visible */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full p-4 flex items-start justify-between gap-3 text-left hover:bg-black/5 rounded-xl transition"
        aria-expanded={abierto}
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="rounded-full bg-primary/10 p-2 shrink-0">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-800">
              Paso {paso.id}: {paso.titulo}
            </h3>
            <div className="mt-1 text-sm text-slate-700">{resumen}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <EstadoChip estado={estado} />
          {abierto ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
        </div>
      </button>

      {/* Contenido expandible */}
      {abierto && (
        <div className="px-4 pb-4">
          {puedeSaltarse && (
            <label className="flex items-start gap-1.5 cursor-pointer text-xs text-slate-600 mb-2">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 mt-0.5"
                checked={saltado}
                onChange={onToggleSaltado}
              />
              <span>
                {saltado ? 'Saltado' : 'Saltar este paso'}
                {hintSaltado && (
                  <span className="block text-slate-400 italic mt-0.5">{hintSaltado}</span>
                )}
              </span>
            </label>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

function EstadoChip({ estado }: { estado: EstadoPaso }) {
  const map: Record<EstadoPaso, { label: string; cls: string }> = {
    hecho:     { label: '✓ Hecho',      cls: 'bg-emerald-600 text-white' },
    pendiente: { label: '⏳ Pendiente', cls: 'bg-amber-100 text-amber-800' },
    saltado:   { label: '⏭ Saltado',   cls: 'bg-slate-300 text-slate-700' },
  };
  const s = map[estado];
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold ${s.cls}`}>{s.label}</span>;
}

/**
 * Sprint 39 — Listado claro de pasos pendientes con accesos directos.
 * Reemplaza el mensaje genérico "Completa o salta los pasos pendientes".
 * TODO(DT-014): componente construido pero aún NO cableado en el wizard.
 * Cablearlo donde hoy se muestra el mensaje genérico, o retirarlo.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function PendientesResumen({
  estadoPaso,
  onIrAPaso,
}: {
  estadoPaso: (id: number) => EstadoPaso;
  onIrAPaso: (id: number) => void;
}) {
  const pendientes = PASOS.filter((p) => estadoPaso(p.id) === 'pendiente');
  if (pendientes.length === 0) return null;
  return (
    <div className="space-y-1">
      <div className="text-sm font-medium text-slate-700">
        Falta{pendientes.length === 1 ? '' : 'n'}:
      </div>
      <ul className="text-sm text-slate-600 space-y-0.5">
        {pendientes.map((p) => (
          <li key={p.id} className="flex items-center gap-2">
            <span className="text-amber-600">•</span>
            <button
              onClick={() => onIrAPaso(p.id)}
              className="text-primary underline hover:text-primary/80 text-left"
            >
              Paso {p.id}: {p.titulo}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Mini({ label, valor }: { label: string; valor: string }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-2 text-center">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="font-mono font-bold text-slate-800 text-sm">{valor}</div>
    </div>
  );
}

// ============================================================================
// PromosAplicadasHoy — listado compacto en el paso 1.
// ============================================================================

function PromosAplicadasHoy({
  promos,
  monedaSimbolo,
  locale,
}: {
  promos: AplicacionPromoVista[];
  monedaSimbolo: string;
  locale: string;
}) {
  if (promos.length === 0) {
    return (
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-500 italic">
        Sin promociones aplicadas hoy.
      </div>
    );
  }
  // Agrupar por nombre+tipo y contar.
  const agrupado = new Map<string, { nombre: string; tipo: string; veces: number; descuento: number }>();
  for (const p of promos) {
    const key = `${p.nombreSnapshot}__${p.tipoSnapshot}`;
    const existing = agrupado.get(key);
    if (existing) {
      existing.veces++;
      existing.descuento += p.descuentoAplicado;
    } else {
      agrupado.set(key, { nombre: p.nombreSnapshot, tipo: p.tipoSnapshot, veces: 1, descuento: p.descuentoAplicado });
    }
  }
  const lista = [...agrupado.values()].sort((a, b) => b.veces - a.veces);
  const totalDescuento = lista.reduce((acc, x) => acc + x.descuento, 0);
  return (
    <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 space-y-1">
      <div className="text-xs font-semibold text-amber-800 flex items-center gap-1">
        <Sparkles className="h-3.5 w-3.5" />
        Promociones aplicadas hoy ({promos.length})
      </div>
      <ul className="text-[11px] text-amber-900 space-y-0.5">
        {lista.map((p) => (
          <li key={p.nombre} className="flex justify-between gap-2">
            <span className="truncate">
              <strong>{p.veces}×</strong> {p.nombre}
            </span>
            {p.descuento > 0 && (
              <span className="font-mono shrink-0">
                −{formatPrecio(p.descuento, monedaSimbolo, locale)}
              </span>
            )}
          </li>
        ))}
      </ul>
      {totalDescuento > 0 && (
        <div className="text-[11px] text-amber-900 border-t border-amber-200 pt-1 flex justify-between font-bold">
          <span>Descuento total</span>
          <span className="font-mono">−{formatPrecio(totalDescuento, monedaSimbolo, locale)}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Sprint 42 — VARIANTE C: panel "Día cerrado"
// ============================================================================

/**
 * Panel grande de éxito cuando el día ya está marcado como cerrado.
 * - Banner verde grande con la hora de cierre.
 * - Resumen final del día (ventas, pedidos).
 * - CTAs: "Ir al dashboard" (gigante, azul) + "Ver reporte completo"
 *   + "Revisar pasos del cierre" (link discreto).
 *
 * Esta pantalla es persistente: si la dueña entra mañana, sigue viendo
 * el wizard normal (porque la marca es por-día). Si entra hoy después de
 * cerrar, vuelve a ver este panel.
 */
function DiaCerradoPanel({
  fechaCierre,
  resumen,
  monedaSimbolo,
  locale,
  onIrDashboard,
  onRevisarPasos,
  onVerReporte,
}: {
  fechaCierre: string;
  resumen: ResumenDiaVista | null;
  monedaSimbolo: string;
  locale: string;
  onIrDashboard: () => void;
  onRevisarPasos: () => void;
  onVerReporte: () => void;
}) {
  const fecha = new Date(fechaCierre);
  const horaStr = fecha.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const fechaStr = fecha.toLocaleDateString(locale, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });

  return (
    <div className="max-w-3xl mx-auto space-y-5 py-4">
      {/* Banner verde gigante */}
      <div className="rounded-2xl border-4 border-emerald-500 bg-gradient-to-br from-emerald-50 to-emerald-100 p-8 text-center shadow-md">
        <div className="flex justify-center mb-3">
          <div className="rounded-full bg-emerald-600 p-4 shadow-lg">
            <CheckCircle2 className="h-12 w-12 text-white" strokeWidth={2.5} />
          </div>
        </div>
        <h2 className="text-3xl font-bold text-emerald-900">Día cerrado correctamente</h2>
        <p className="text-emerald-800 mt-2 capitalize">{fechaStr}</p>
        <p className="text-sm text-emerald-700 mt-1">
          Marcado a las <strong className="font-mono">{horaStr}</strong>
        </p>
      </div>

      {/* Resumen final del día */}
      {resumen && (
        <div className="card border-2 border-emerald-200">
          <h3 className="font-semibold text-sm text-slate-700 mb-3 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-emerald-700" />
            {/*
              Sprint 144 (auditoría) — "de hoy", no "del día". Estas cifras son del día de
              calendario, y aparecen segundos después de que la tarjeta mostrara lo acumulado desde
              que se abrió la caja: sin la palabra, son dos totales distintos sin explicación. Es el
              último sitio donde quedaba ese eco.
            */}
            Resumen de hoy
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-xs uppercase text-slate-500">Ventas</div>
              <div className="font-mono text-lg font-bold text-slate-800">
                {formatPrecio(resumen.totalVentas, monedaSimbolo, locale)}
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-xs uppercase text-slate-500">Pedidos</div>
              <div className="font-mono text-lg font-bold text-slate-800">
                {resumen.cantidadPedidosPagados}
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-xs uppercase text-slate-500">Ticket prom.</div>
              <div className="font-mono text-lg font-bold text-slate-800">
                {formatPrecio(resumen.ticketPromedio, monedaSimbolo, locale)}
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 p-3">
              <div className="text-xs uppercase text-slate-500">Efectivo</div>
              <div className="font-mono text-lg font-bold text-slate-800">
                {formatPrecio(resumen.totalEfectivo, monedaSimbolo, locale)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTAs */}
      <div className="grid gap-3 sm:grid-cols-2">
        <button
          onClick={onIrDashboard}
          className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-4 text-lg font-semibold text-white shadow hover:bg-blue-700 active:scale-[0.98] transition"
        >
          <Home className="h-6 w-6" />
          Ir al inicio
        </button>
        <button
          onClick={onVerReporte}
          className="flex items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-5 py-4 text-lg font-semibold text-slate-800 hover:bg-slate-50 active:scale-[0.98] transition"
        >
          <Receipt className="h-6 w-6" />
          Ver reporte completo
        </button>
      </div>

      <div className="text-center">
        <button
          onClick={onRevisarPasos}
          className="text-sm text-slate-500 hover:text-slate-800 underline"
        >
          Revisar los pasos del cierre
        </button>
      </div>

      {/* Buenas noches */}
      <div className="text-center text-sm text-slate-500 flex items-center justify-center gap-2 pt-2">
        <Sun className="h-4 w-4 text-amber-500" />
        Buena jornada mañana 🎉
      </div>
    </div>
  );
}

// =============================================================================
// Sprint 58 — Actividad sin cerrar
// =============================================================================

/**
 * Reporte de campo (2026-08-02): una mesa llevaba 16 h abierta —desde el día
 * anterior— y el cierre del día no la mencionaba, porque la advertencia sólo
 * contaba pedidos en cocina. Un día laboral no arrastra consumos del día
 * anterior: si algo sigue vivo, hay que decir **dónde** está para ir por él.
 */

/** "16 h 15 min" / "45 min". El operador no debe restar horas mentalmente. */
function tiempoLegible(minutos: number): string {
  if (minutos < 60) return `${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Resumen de una línea: "2 mesas y 1 entrega". */
function resumirPendientes(p: PendientesCierre): string {
  const partes: string[] = [];
  if (p.mesas.length > 0) partes.push(`${p.mesas.length} mesa${p.mesas.length !== 1 ? 's' : ''}`);
  if (p.cocina.length > 0) partes.push(`${p.cocina.length} en cocina`);
  if (p.delivery.length > 0) {
    partes.push(`${p.delivery.length} entrega${p.delivery.length !== 1 ? 's' : ''}`);
  }
  if (partes.length === 0) return 'nada';
  if (partes.length === 1) return partes[0];
  return `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`;
}

/**
 * Sprint 137 — Los domicilios que ya se entregaron y siguen sin cobrarse.
 *
 * No bloquea el cierre, y esa es la decisión de fondo: en el arreglo real del negocio el
 * repartidor puede regresar a pagar mañana, así que frenar el día por esto sería un estorbo
 * diario — y ya sabemos cómo termina, *"a como puedo cierro el día y si no puedo, pues ahí se
 * queda abierto"* (el fracaso del 23/05).
 *
 * Lo que sí hace es **nombrar el dinero y decir con quién está**. Un día que cierra sin mencionar
 * que hay $930 en la calle es un día que cuadra mintiendo.
 */
function PorCobrarEnLaCallePanel({
  items,
  monto,
  monedaSimbolo,
  locale,
  onIrADomicilios,
}: {
  items: PendienteCierre[];
  monto: number;
  monedaSimbolo: string;
  locale: string;
  onIrADomicilios: () => void;
}) {
  return (
    <div className="rounded-lg border-2 border-violet-300 bg-violet-50 p-4">
      <div className="flex gap-2">
        <Bike className="mt-0.5 h-5 w-5 shrink-0 text-violet-700" />
        <div className="flex-1">
          <p className="font-semibold text-violet-900">
            {items.length === 1
              ? '1 pedido se entregó y todavía no te lo pagan'
              : `${items.length} pedidos se entregaron y todavía no te los pagan`}{' '}
            — {formatPrecio(monto, monedaSimbolo, locale)}
          </p>
          <p className="mt-0.5 text-sm text-violet-900">
            <b>Puedes cerrar el día igual.</b> Ese dinero no se cuenta como cobrado: queda
            apuntado hasta que entre.
          </p>
          <ul className="mt-2 space-y-1">
            {items.map((p) => (
              <li key={p.pedidoId} className="text-sm text-violet-900">
                • #{String(p.numeroPedido).padStart(5, '0')} — {p.referencia} ·{' '}
                <span className="font-mono font-semibold">
                  {formatPrecio(p.total, monedaSimbolo, locale)}
                </span>
              </li>
            ))}
          </ul>
          <button
            onClick={onIrADomicilios}
            className="btn-ghost mt-2 min-h-[44px] border border-violet-300 text-sm text-violet-900"
          >
            Ir a cobrarlos →
          </button>
        </div>
      </div>
    </div>
  );
}

function PendientesCierrePanel({
  pendientes,
  onIrAPedidos,
}: {
  pendientes: PendientesCierre;
  onIrAPedidos: () => void;
}) {
  const grupos: Array<{ titulo: string; items: PendienteCierre[] }> = [
    { titulo: 'Mesas ocupadas', items: pendientes.mesas },
    { titulo: 'En cocina', items: pendientes.cocina },
    { titulo: 'Entregas a domicilio', items: pendientes.delivery },
  ].filter((g) => g.items.length > 0);

  return (
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
      <div className="flex gap-2">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-700 mt-0.5" />
        <div className="flex-1">
          <p className="font-semibold text-amber-900">
            Quedó {resumirPendientes(pendientes)} sin cerrar.
          </p>
          <p className="mt-0.5 text-sm text-amber-900">
            Cobra o cancela esto antes de cerrar el día. Si cierras así, el consumo se queda
            arrastrando al día siguiente.
          </p>

          {pendientes.deDiasAnteriores > 0 && (
            <p className="mt-2 rounded-md bg-red-100 px-3 py-2 text-sm font-medium text-red-900">
              {pendientes.deDiasAnteriores === 1
                ? '1 de estos viene de un día anterior.'
                : `${pendientes.deDiasAnteriores} de estos vienen de días anteriores.`}{' '}
              Una mesa no puede durar más de una jornada: seguramente se olvidó cobrarla.
            </p>
          )}

          <div className="mt-3 space-y-3">
            {grupos.map((g) => (
              <div key={g.titulo}>
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                  {g.titulo}
                </p>
                <ul className="mt-1 space-y-1">
                  {g.items.map((p) => (
                    <li
                      key={p.pedidoId}
                      className="flex flex-wrap items-baseline gap-x-2 text-sm text-amber-900"
                    >
                      <span className="font-medium">{p.referencia}</span>
                      <span className="text-amber-700">
                        #{String(p.numeroPedido).padStart(5, '0')} · abierto hace{' '}
                        {tiempoLegible(p.minutosAbierto)}
                      </span>
                      {p.deDiaAnterior && (
                        <span className="rounded bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white">
                          de otro día
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <button
            onClick={onIrAPedidos}
            className="mt-3 min-h-[44px] rounded-lg bg-amber-600 px-4 font-semibold text-white hover:bg-amber-700 active:scale-[0.98] transition"
          >
            Ir a Pedidos para cerrarlos →
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Sprint 144 — **"Ya me voy": cerrar el día en un botón.**
 *
 * ## De dónde sale
 *
 * De ocho días sin un solo cierre en la operación real, y de las dos razones que dio el dueño:
 * que en su sistema anterior el día se cerraba solo al abrir el siguiente, y que **este cierre es
 * laborioso justo cuando ya se quiere ir**.
 *
 * ## Qué hace, y qué deliberadamente NO hace
 *
 * Encadena las dos únicas cosas que cierran el día de verdad —cortar la caja y marcarlo cerrado—
 * bajo **una sola confirmación**. El código de seguridad se sigue pidiendo: la ventana de cinco
 * minutos que ya existía desde el Sprint 32 cubre las dos llamadas porque ahora van seguidas, en
 * segundos, en vez de separadas por todo el trabajo de en medio. **No se tocó ningún candado**
 * para conseguirlo, y es lo que hace este atajo aceptable.
 *
 * Los cuatro pasos siguen abajo, enteros. Quien quiera repartir propinas o cerrar existencias
 * entra y lo hace; lo que cambia es que dejan de ser el camino para poder apagar la luz.
 *
 * El conteo del efectivo se pide **una vez y como una sola cifra**, y es opcional: contar billete
 * por billete a las once de la noche es donde más errores se cometen, y un cierre sin conteo vale
 * infinitamente más que ocho días sin cierre.
 */
function CerrarMiDiaCard({
  corte,
  hayPendientes,
  cuantosPendientes,
  monedaSimbolo,
  locale,
  onIrAPedidos,
  onListo,
}: {
  corte: CorteCajaVista;
  hayPendientes: boolean;
  cuantosPendientes: number;
  monedaSimbolo: string;
  locale: string;
  onIrAPedidos: () => void;
  onListo: () => Promise<void>;
}) {
  const [contado, setContado] = useState('');
  const [cerrando, setCerrando] = useState(false);
  /*
    Sprint 144 (auditoría de interfaz) — Las dos cifras salen de la MISMA consulta y del mismo
    periodo. "Vendiste" venía del día de calendario y "Debe haber en la caja" de lo acumulado desde
    que se abrió la caja: con la caja de Claudia abierta ocho días, eran dos relojes distintos uno
    al lado del otro. Es **el mismo defecto que el Sprint 141 acababa de arreglar** en la pantalla
    del corte, repetido aquí por no mirar lo que el servidor ya devolvía.
  */
  const [datos, setDatos] = useState<{
    efectivoEsperado: number;
    totalVentas: number;
    desde: string;
  } | null>(null);
  const [fallo, setFallo] = useState(false);
  /** Segundo toque: cerrar no tiene vuelta atrás y este botón es la única fricción que queda. */
  const [confirmando, setConfirmando] = useState(false);
  /**
   * Sprint 144 (segunda auditoría) — **El segundo toque se saltaba con un doble clic.**
   *
   * Sin guarda de tiempo, dos pulsaciones a 150 ms —el gesto de quien cree que el botón no
   * respondió, documentado en este mismo proyecto desde el Sprint 43— armaban la confirmación y
   * cerraban el día de corrido. Y hoy no hay nada detrás: **nadie en este negocio tiene el segundo
   * factor configurado**, así que ésta es toda la fricción que existe antes de una acción
   * irreversible.
   */
  const [listoParaConfirmar, setListoParaConfirmar] = useState(false);
  /** Por qué no cuadró. Va al papel del corte, que si no sale con un número mudo. */
  const [motivo, setMotivo] = useState('');
  /*
    ¿A esta persona se le va a pedir el código? Se pregunta para no prometer un paso que no va a
    ocurrir: un aviso que no se cumple enseña a no leer los avisos.
  */
  const [pediraCodigo, setPediraCodigo] = useState(false);
  const sesion = useSessionStore((x) => x.sesion);
  const stepUp = useStepUp<unknown>();

  /*
    El efectivo que debe haber en la gaveta lo calcula el servidor, y se pide aquí en vez de
    derivarlo: es la regla que dejó el Sprint 70 (DT-068) después de que la pantalla y el cierre
    contaran periodos distintos y "corregir" el conteo grabara un faltante que no existía.
  */
  useEffect(() => {
    let vivo = true;
    void window.api.corte
      .esperado()
      .then((r) => {
        if (!vivo) return;
        if (r.ok && r.data) setDatos(r.data);
        else setFallo(true);
      })
      .catch(() => {
        // Un cero inventado sobre dinero no es un estado de carga: se dice que no se pudo.
        if (vivo) setFallo(true);
      });
    if (sesion?.usuarioId != null && sesion.rol === 'dueño') {
      void window.api.usuarios.totpEstado(sesion.usuarioId).then((r) => {
        if (vivo && r.ok) setPediraCodigo(r.data.enabled === true);
      });
    }
    return () => {
      vivo = false;
    };
  }, [corte.id, sesion?.usuarioId, sesion?.rol]);
  const esperado = datos?.efectivoEsperado ?? 0;
  /*
    Sprint 144 (auditoría) — `Number(texto.replace(',', '.'))` entendía "2,700" como **$2.70** y
    "2,785.00" como cero, y el `|| 0` grababa ese disparate en un corte que no tiene vuelta atrás.
    Ahora lo lee `leerDineroTecleado`, que además distingue "no contó" de "escribió algo raro".
  */
  const lectura = leerDineroTecleado(contado);
  const contadoCentavos = lectura.ok ? lectura.centavos : null;
  const conteoIlegible = !lectura.ok && lectura.motivo === 'no_es_numero';
  const diferencia =
    contadoCentavos === null || datos === null ? null : contadoCentavos - esperado;

  /*
    Medio segundo largo de cortesía: suficiente para que un doble clic no pase, corto para que no
    se sienta un botón roto. Y `confirmando` se cae en cuanto ella toca el conteo o el motivo: si
    cambió lo que iba a firmar, la firma ya no vale.
  */
  useEffect(() => {
    if (!confirmando) {
      setListoParaConfirmar(false);
      return;
    }
    const t = setTimeout(() => setListoParaConfirmar(true), 700);
    return () => clearTimeout(t);
  }, [confirmando]);

  useEffect(() => {
    setConfirmando(false);
  }, [contado, motivo]);

  const cerrar = async () => {
    setCerrando(true);
    /*
      Sprint 144 (auditoría) — La diferencia se **nombra**, no sólo se guarda.

      `observaciones` iba fija a "Cierre rápido del día" y pisaba el único campo donde cabe el
      motivo: el PDF del corte salía con un faltante que nadie podría explicar después. El dominio
      lo pide con todas sus letras —contar, comparar, **nombrar la diferencia** y firmarla—, así
      que lo que ella escriba se concatena en vez de sustituir.
    */
    const nota = [
      'Cierre rápido del día',
      motivo.trim() ? `Diferencia: ${motivo.trim()}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    /*
      Las dos llamadas van juntas y en este orden: si el corte falla, el día NO se marca cerrado.
      Al revés dejaría un día "cerrado" con la caja abierta, que es peor que no haber empezado.
    */
    const res = await stepUp.ejecutar(() =>
      window.api.corte.cerrar(corte.id, {
        ...(contadoCentavos !== null ? { montoFinalDeclarado: contadoCentavos } : {}),
        observaciones: nota,
      }),
    );
    if (!res.ok) {
      setCerrando(false);
      /*
        Ni el modal del código ni haberlo cancelado son errores que gritar: el primero ya abrió su
        ventana y el segundo es una decisión suya. Alertar ahí enseña a temerle al botón.
      */
      if (res.codigo !== 'STEP_UP_REQUIRED' && res.codigo !== 'CANCELADO') {
        window.alertar({ titulo: 'No se pudo cerrar la caja', mensaje: res.error, variant: 'warn' });
      }
      return;
    }
    const marca = await window.api.cierreDia.marcarCerrado();
    setCerrando(false);
    if (!marca.ok) {
      window.alertar({
        titulo: 'La caja quedó cerrada, pero el día no',
        mensaje: `${marca.error} Puedes terminarlo desde los pasos de abajo.`,
        variant: 'warn',
      });
    }
    await onListo();
  };

  return (
    <div className="rounded-xl border-2 border-primary bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <Sun aria-hidden="true" className="mt-0.5 h-6 w-6 shrink-0 text-primary" />
        <div className="flex-1">
          <h2 className="text-lg font-bold">Terminar mi día</h2>
          <p className="text-sm text-slate-600">
            Cierra la caja y el día de una vez. Lo demás de abajo es opcional.
          </p>
        </div>
      </div>

      {/*
        Sprint 144 (auditoría) — Las dos cifras del MISMO reloj, y el periodo dicho con todas sus
        letras. Antes "Vendiste" era el día de calendario y "Debe haber" lo acumulado desde que se
        abrió la caja: con ocho días abierta, dos verdades distintas presentadas como gemelas.
      */}
      <p className="mt-3 text-sm font-semibold text-slate-700">
        {datos
          ? `Desde que abriste la caja · ${new Date(datos.desde).toLocaleDateString(locale, {
              weekday: 'short',
              day: '2-digit',
              month: 'short',
            })}`
          : 'Desde que abriste la caja'}
      </p>
      <div className="mt-1 grid gap-2 sm:grid-cols-2">
        <div className="rounded-lg bg-white px-3 py-2">
          <div className="text-xs font-semibold text-slate-600">Vendiste</div>
          <div className="font-mono text-xl font-bold">
            {datos ? formatPrecio(datos.totalVentas, monedaSimbolo, locale) : '—'}
          </div>
        </div>
        <div className="rounded-lg bg-white px-3 py-2">
          <div className="text-xs font-semibold text-slate-600">Debe haber en caja</div>
          <div className="font-mono text-xl font-bold">
            {datos ? formatPrecio(esperado, monedaSimbolo, locale) : '—'}
          </div>
        </div>
      </div>

      {/*
        Un cero inventado sobre dinero no es un estado de carga. Mientras no se sepa cuánto debe
        haber, no se pregunta cuánto hay: comparar contra un número que no llegó sólo produce
        faltantes imaginarios.
      */}
      {fallo ? (
        <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
          No pude calcular cuánto debe haber en la caja. Puedes cerrar igual, o mirar el detalle en
          el Corte de Caja de abajo.
        </p>
      ) : (
        <div className="mt-3">
          <label className="label" htmlFor="contadoRapido">
            ¿Cuánto hay en la caja?{' '}
            <span className="font-normal text-slate-600">(si quieres contarlo)</span>
          </label>
          <div className="relative max-w-[240px]">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-mono text-slate-600">
              {monedaSimbolo}
            </span>
            <input
              id="contadoRapido"
              className="input pl-8 font-mono"
              inputMode="decimal"
              value={contado}
              onChange={(e) => setContado(e.target.value)}
              /*
                Al salir del campo se escribe como el sistema lo entendió. Es la confirmación más
                barata de que el lector acertó: ella ve "2,700.00" donde tecleó "2700", y si
                hubiera entendido otra cosa lo vería aquí, no en el papel del corte.
              */
              onBlur={() => {
                const l = leerDineroTecleado(contado);
                // Con su separador de millar, como el resto de las cifras. El lector lo acepta de vuelta.
                if (l.ok) setContado(formatPrecio(l.centavos, '', locale));
              }}
              disabled={datos === null}
              aria-invalid={conteoIlegible}
              aria-describedby={conteoIlegible ? 'contadoError' : undefined}
              placeholder="Lo que contaste"
            />
          </div>
          {conteoIlegible && (
            <p id="contadoError" className="mt-1 text-sm font-semibold text-red-700">
              No entendí esa cantidad. Escríbela así: 2785 o 2,785.00
            </p>
          )}
          {diferencia !== null && diferencia !== 0 && (
            <>
              <p
                className={`mt-1 text-sm font-semibold ${
                  diferencia > 0 ? 'text-emerald-700' : 'text-red-700'
                }`}
              >
                {diferencia > 0 ? 'Sobra ' : 'Falta '}
                {formatPrecio(Math.abs(diferencia), monedaSimbolo, locale)}
                <span className="font-normal text-slate-700">
                  {' '}
                  — se guarda anotado, puedes cerrar igual.
                </span>
              </p>
              {/* Una diferencia sin motivo es un número mudo en el papel del corte. */}
              <label className="label mt-2" htmlFor="motivoDif">
                ¿Por qué crees que {diferencia > 0 ? 'sobra' : 'falta'}?{' '}
                <span className="font-normal text-slate-600">(opcional)</span>
              </label>
              <input
                id="motivoDif"
                className="input max-w-[640px]"
                value={motivo}
                maxLength={120}
                onChange={(e) => setMotivo(e.target.value)}
                placeholder="Le di cambio de más, pagué al del gas…"
              />
            </>
          )}
        </div>
      )}

      {hayPendientes ? (
        /*
          Con pedidos abiertos no se cierra: su consumo se arrastraría al día siguiente. Se dice
          qué falta —y cuántos son, que el dato está a mano— en vez de un botón apagado.
        */
        <button
          onClick={onIrAPedidos}
          className="btn-ghost mt-3 min-h-[44px] w-full border border-amber-300 bg-amber-50 text-amber-900"
        >
          {cuantosPendientes === 1
            ? 'Primero hay que cerrar el pedido que sigue abierto →'
            : `Primero hay que cerrar los ${cuantosPendientes} pedidos que siguen abiertos →`}
        </button>
      ) : (
        <>
          {/*
            Sprint 144 (auditoría) — **El segundo toque.**

            Esto cierra el corte, marca el día y emite el papel, y no hay forma de reabrirlo. El
            camino largo pedía dos confirmaciones; el corto se quedaba en una sola pulsación, y
            para quien no tiene 2FA configurado —que hoy es el caso del cliente— eso significa
            **ninguna fricción** antes de una acción irreversible.

            La fricción va en el propio botón y no en una ventana: el Sprint 43 ya documentó que
            el `window.confirmar` de esta pantalla se leía como "el botón no hace nada". Y no se
            pinta de rojo: hace falta un segundo de atención, no un susto.
          */}
          {confirmando && !cerrando && (
            <div role="status" className="mt-3 rounded-lg bg-white px-3 py-2 text-sm text-slate-700">
              <p>Después ya no vas a poder cambiar los pedidos de hoy.</p>
              {diferencia !== null && diferencia !== 0 && (
                <p className="mt-1 font-semibold">
                  Queda anotado que {diferencia > 0 ? 'sobran' : 'faltan'}{' '}
                  {formatPrecio(Math.abs(diferencia), monedaSimbolo, locale)}.
                </p>
              )}
            </div>
          )}
          <button
            onClick={() => (confirmando ? void cerrar() : setConfirmando(true))}
            disabled={cerrando || conteoIlegible || (confirmando && !listoParaConfirmar)}
            className="btn-primary mt-3 min-h-[52px] w-full text-base disabled:opacity-50"
          >
            {cerrando
              ? 'Cerrando…'
              : confirmando
                ? 'Sí, cerrar mi caja y mi día'
                : 'Terminar mi día'}
          </button>
          {confirmando && !cerrando && (
            /*
              Sprint 144 (segunda auditoría) — Arrepentirse tiene que costar lo mismo que seguir.

              "Mejor no" era un enlace de 67 × 18 px dentro de un párrafo: la única salida de una
              acción irreversible, y el control más pequeño de la tarjeta. Ahora es un botón de
              verdad, en su propia línea.
            */
            <button
              type="button"
              onClick={() => setConfirmando(false)}
              className="btn-ghost mt-2 min-h-[44px] w-full border border-slate-500"
              /* `slate-300` sobre el azul tenue de la tarjeta daba 1.33:1: se leía el texto
                 pero no se veía que fuera un botón. `slate-500` da 4.25:1 sobre ese mismo
                 fondo. Ojo para quien copie el patrón: `.btn-secondary` NO lo arregla aquí. */
            >
              Mejor no
            </button>
          )}
          {conteoIlegible && (
            <p className="mt-2 text-center text-sm text-red-700">
              Arregla lo que hay en la caja para poder cerrar.
            </p>
          )}
          {/* Que no lo descubra al final: el teléfono se busca antes, no con el botón ya apretado. */}
          {pediraCodigo && !confirmando && (
            <p className="mt-2 text-center text-xs text-slate-600">
              Te va a pedir el código de 6 dígitos de tu teléfono.
            </p>
          )}
        </>
      )}

      <StepUpModal
        abierto={stepUp.modalAbierto}
        onConfirmado={stepUp.onConfirmado}
        onCancelar={stepUp.onCancelar}
      />
    </div>
  );
}
